import { HTMLAttributes, forwardRef, ReactNode } from 'react';
import './Card.css';

export type CardPadding = 'none' | 'sm' | 'md' | 'lg';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Padding inside the card */
  padding?: CardPadding;
  /** Add shadow to the card */
  shadow?: boolean;
  /** Make the card hoverable */
  hoverable?: boolean;
}

export interface CardHeaderProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  'title'
> {
  /** Title of the card */
  title?: ReactNode;
  /** Subtitle or description */
  subtitle?: ReactNode;
  /** Actions to display on the right */
  actions?: ReactNode;
}

export interface CardBodyProps extends HTMLAttributes<HTMLDivElement> {}

export interface CardFooterProps extends HTMLAttributes<HTMLDivElement> {}

/**
 * Card component for grouping related content.
 *
 * @example
 * ```tsx
 * <Card>
 *   <Card.Header title="Title" subtitle="Subtitle" />
 *   <Card.Body>Content goes here</Card.Body>
 *   <Card.Footer>Footer content</Card.Footer>
 * </Card>
 * ```
 */
export const Card = forwardRef<HTMLDivElement, CardProps>(
  (
    {
      padding = 'md',
      shadow = false,
      hoverable = false,
      className = '',
      children,
      ...props
    },
    ref
  ) => {
    const classNames = [
      'ui-card',
      `ui-card--padding-${padding}`,
      shadow && 'ui-card--shadow',
      hoverable && 'ui-card--hoverable',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div ref={ref} className={classNames} {...props}>
        {children}
      </div>
    );
  }
) as CardComponent;

Card.displayName = 'Card';

const CardHeader = forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ title, subtitle, actions, className = '', children, ...props }, ref) => {
    const classNames = ['ui-card__header', className].filter(Boolean).join(' ');

    return (
      <div ref={ref} className={classNames} {...props}>
        {(title || subtitle) && (
          <div className="ui-card__header-content">
            {title && <h3 className="ui-card__title">{title}</h3>}
            {subtitle && <p className="ui-card__subtitle">{subtitle}</p>}
          </div>
        )}
        {children}
        {actions && <div className="ui-card__header-actions">{actions}</div>}
      </div>
    );
  }
);

CardHeader.displayName = 'Card.Header';

const CardBody = forwardRef<HTMLDivElement, CardBodyProps>(
  ({ className = '', children, ...props }, ref) => {
    const classNames = ['ui-card__body', className].filter(Boolean).join(' ');

    return (
      <div ref={ref} className={classNames} {...props}>
        {children}
      </div>
    );
  }
);

CardBody.displayName = 'Card.Body';

const CardFooter = forwardRef<HTMLDivElement, CardFooterProps>(
  ({ className = '', children, ...props }, ref) => {
    const classNames = ['ui-card__footer', className].filter(Boolean).join(' ');

    return (
      <div ref={ref} className={classNames} {...props}>
        {children}
      </div>
    );
  }
);

CardFooter.displayName = 'Card.Footer';

// Compound component type
interface CardComponent extends React.ForwardRefExoticComponent<
  CardProps & React.RefAttributes<HTMLDivElement>
> {
  Header: typeof CardHeader;
  Body: typeof CardBody;
  Footer: typeof CardFooter;
}

// Attach sub-components
Card.Header = CardHeader;
Card.Body = CardBody;
Card.Footer = CardFooter;
